import { useState } from "react";
import { formatDateNumeric } from "../../utils/formatters";
import {
  Stack,
  Card,
  Table,
  Badge,
  Group,
  Text,
  Select,
  Alert,
  Loader,
  Progress,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  Button,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconInfoCircle,
  IconEye,
  IconBan,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useFacilityContext } from "../../hooks/useFacilityContext";
import { useFacilities } from "../../hooks/useFacilities";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../lib/apiClient";
import { useRevokeAuditGrant } from "../../hooks/useAuditorGrants";

interface ActiveAudit {
  grant_id: string;
  auditor_name: string;
  /** Null when neither the request nor the employee record carries one. */
  auditor_email: string | null;
  auditor_organization: string | null;
  audit_type: string;
  facility_ids: string[];
  /** Calendar dates (YYYY-MM-DD) — see the route's dateOnly note (F-082). */
  valid_from: string;
  valid_until: string;
  /** EVERY scope the grant carries, not just the first (F-091). */
  access_scopes: string[];
  days_remaining: number;
  progress_percentage: number;
}

/**
 * Per-row revoke control. Owns its own `useRevokeAuditGrant` instance (the hook
 * is keyed by grant id) plus a confirm modal that captures the required
 * `revocation_reason`.
 */
function RevokeGrantControl({
  grantId,
  auditorLabel,
  selectedFacilityId,
}: {
  grantId: string;
  auditorLabel: string;
  selectedFacilityId: string;
}) {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const revoke = useRevokeAuditGrant(grantId);

  const close = () => {
    setOpened(false);
    setReason("");
    setError(null);
  };

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError("A revocation reason is required");
      return;
    }
    try {
      await revoke.mutateAsync({ revocation_reason: reason.trim() });
      notifications.show({
        title: "Access revoked",
        message: `Audit access for ${auditorLabel} has been revoked.`,
        color: "green",
      });
      queryClient.invalidateQueries({
        queryKey: ["active-audits", selectedFacilityId],
      });
      close();
    } catch {
      // error toast comes from the global mutation handler (US-081)
    }
  };

  return (
    <>
      <Tooltip label="Revoke access" withArrow>
        <ActionIcon
          variant="light"
          color="red"
          aria-label="Open revoke access dialog"
          onClick={() => setOpened(true)}
        >
          <IconBan size={16} />
        </ActionIcon>
      </Tooltip>

      <Modal opened={opened} onClose={close} title="Revoke Audit Access">
        <Stack gap="md">
          <Text size="sm">
            This will immediately revoke {auditorLabel}'s access to audit this
            facility. This action cannot be undone.
          </Text>
          <Textarea
            label="Revocation reason"
            placeholder="Explain why access is being revoked..."
            required
            minRows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.currentTarget.value);
              if (error) setError(null);
            }}
            error={error}
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={close}
              disabled={revoke.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirm}
              loading={revoke.isPending}
            >
              Revoke Access
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * Active Audits Tab
 * Shows all currently active audits in progress
 */
export function ActiveAuditsTab() {
  const { currentFacilityId, facilityIds } = useFacilityContext();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(
    currentFacilityId || facilityIds[0] || "",
  );

  // Fetch facilities to get names
  const { data: facilities } = useFacilities();

  const {
    data: activeAudits,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["active-audits", selectedFacilityId],
    queryFn: async () => {
      const { data } = await api.get(
        `/facilities/${selectedFacilityId}/active-audits`,
      );
      return data.data as ActiveAudit[];
    },
    enabled: !!selectedFacilityId,
  });

  const getProgressColor = (daysRemaining: number) => {
    if (daysRemaining > 14) return "green";
    if (daysRemaining > 7) return "yellow";
    return "red";
  };

  // Create facility options with names
  const facilityOptions =
    facilities
      ?.filter((f) => facilityIds.includes(f.id))
      .map((f) => ({ value: f.id, label: f.name })) || [];

  return (
    <Stack gap="md">
      {/* Active Audits Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Monitor active audits in progress. Auditors with active grants have
        read-only access to specified facilities and data.
      </Alert>

      {/* Filters */}
      {facilityIds.length > 1 && (
        <Card withBorder>
          <Select
            label="Facility"
            placeholder="Select facility"
            data={facilityOptions}
            value={selectedFacilityId}
            onChange={(value) => setSelectedFacilityId(value || facilityIds[0])}
            style={{ maxWidth: 300 }}
          />
        </Card>
      )}

      {/* Active Audits Table */}
      <Card withBorder>
        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading active audits...</Text>
          </Stack>
        ) : isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load active audits. Please try refreshing the page.
          </Alert>
        ) : !activeAudits || activeAudits.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <IconEye size={48} stroke={1.5} color="gray" />
            <Text c="dimmed">No active audits in progress</Text>
            <Text size="sm" c="dimmed">
              Approved audit requests will appear here when auditors begin their
              work
            </Text>
          </Stack>
        ) : (
          <>
            <Table.ScrollContainer minWidth={1000}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: "left" }}>Auditor</Table.Th>
                    <Table.Th style={{ textAlign: "left" }}>
                      Organization
                    </Table.Th>
                    <Table.Th style={{ textAlign: "left" }}>
                      Audit Type
                    </Table.Th>
                    <Table.Th style={{ textAlign: "left" }}>
                      Access Level
                    </Table.Th>
                    <Table.Th style={{ textAlign: "left" }}>
                      Valid Period
                    </Table.Th>
                    <Table.Th style={{ textAlign: "left" }}>
                      Time Remaining
                    </Table.Th>
                    <Table.Th style={{ textAlign: "left", width: 120 }}>
                      Actions
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {activeAudits.map((audit) => (
                    <Table.Tr key={audit.grant_id}>
                      <Table.Td>
                        <Stack gap={2}>
                          <Text size="sm" fw={500}>
                            {audit.auditor_name || audit.auditor_email}
                          </Text>
                          {audit.auditor_email &&
                            audit.auditor_email !== audit.auditor_name && (
                              <Text size="xs" c="dimmed">
                                {audit.auditor_email}
                              </Text>
                            )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          c={audit.auditor_organization ? undefined : "dimmed"}
                        >
                          {audit.auditor_organization ?? "Not given"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" tt="capitalize">
                          {audit.audit_type.replace("_", " ")}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {audit.access_scopes.length === 0 ? (
                          <Text size="sm" c="dimmed">
                            None
                          </Text>
                        ) : (
                          <Group gap={4}>
                            {audit.access_scopes.map((scope) => (
                              <Badge
                                key={scope}
                                variant="light"
                                tt="capitalize"
                              >
                                {scope.replace(/_/g, " ")}
                              </Badge>
                            ))}
                          </Group>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={4}>
                          <Text size="sm">
                            {formatDateNumeric(audit.valid_from)}
                            {" - "}
                            {formatDateNumeric(audit.valid_until)}
                          </Text>
                          <Progress
                            value={audit.progress_percentage}
                            color={getProgressColor(audit.days_remaining)}
                            size="xs"
                          />
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getProgressColor(audit.days_remaining)}>
                          {audit.days_remaining} days
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <RevokeGrantControl
                            grantId={audit.grant_id}
                            auditorLabel={
                              audit.auditor_name ||
                              audit.auditor_email ||
                              "this auditor"
                            }
                            selectedFacilityId={selectedFacilityId}
                          />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {/* Summary */}
            <Card withBorder mt="md">
              <Group>
                <Text size="sm" c="dimmed">
                  Total Active: {activeAudits.length}
                </Text>
                <Text size="sm" c="dimmed">
                  •
                </Text>
                <Text size="sm" c="dimmed">
                  Expiring Soon (≤7 days):{" "}
                  {activeAudits.filter((a) => a.days_remaining <= 7).length}
                </Text>
              </Group>
            </Card>
          </>
        )}
      </Card>
    </Stack>
  );
}
